import { useState } from 'react';
import FilterChip from '../../components/ui/FilterChip';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import PasswordField from '../../components/ui/PasswordField';
import Toggle from '../../components/ui/Toggle';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { EyeIcon, Spinner } from '../../components/ui/auth/AuthIcons';
import PassFailToggle from '../../features/tests/components/PassFailToggle';
import { Category, Section, Row, ColumnLabel } from './galleryPrimitives';

/** Forms — text/select/password inputs plus the toggle, segmented, and chip controls. */
export default function FormsGallery() {
  const [chip, setChip] = useState('due');
  const [on, setOn] = useState(true);
  const [seg, setSeg] = useState('light');
  const [passFail, setPassFail] = useState(null);

  return (
    <Category id="forms" title="Forms & inputs">
      <Section name="Input / Select / Textarea / PasswordField" file="components/ui/Input.jsx · Select.jsx · Textarea.jsx · PasswordField.jsx">
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <Input id="g-in" label="Input" placeholder="Asset ID" defaultValue="RCD-014" />
          <Input id="g-in-err" label="Input (error)" defaultValue="" error="Asset ID is required" />
          <Input id="g-in-dis" label="Input (disabled)" defaultValue="Read only" disabled />
          <Input id="g-in-adorn" label="Input (endAdornment)" defaultValue="30" endAdornment={<span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>ms</span>} />
          <Select id="g-sel" label="Select" defaultValue="rcd">
            <option value="rcd">RCD</option>
            <option value="exit">Exit light</option>
          </Select>
          <Select id="g-sel-err" label="Select (error)" defaultValue="" error="Choose a test type">
            <option value="">Select…</option>
            <option value="rcd">RCD</option>
          </Select>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            <ColumnLabel>Select (pill · dark surface, native picker)</ColumnLabel>
            <Select id="g-sel-pill" variant="pill" aria-label="Asset type" defaultValue="rcd">
              <option value="rcd">RCD</option>
              <option value="exit">Exit light</option>
              <option value="db">Distribution board</option>
            </Select>
          </div>
          <PasswordField id="g-pw" label="PasswordField" defaultValue="hunter2000" />
          <Textarea id="g-ta" label="Textarea" defaultValue="Tripped at 22 ms on the 30 mA setting." />
        </div>
      </Section>

      <Section name="Toggle" file="components/ui/Toggle.jsx · features/tests/components/PassFailToggle.jsx">
        <Row label="on / off / disabled">
          <Toggle id="g-t1" checked={on} onChange={setOn} label="Enabled" />
          <Toggle id="g-t2" checked={false} onChange={() => {}} label="Off" />
          <Toggle id="g-t3" checked disabled onChange={() => {}} label="Disabled" />
        </Row>
        <Row label="pass / fail / unselected">
          <div style={{ width: '100%', maxWidth: '20rem' }}>
            <PassFailToggle
              id="g-pass-fail"
              value={passFail}
              onChange={setPassFail}
              ariaLabel="Demo test result"
            />
          </div>
        </Row>
        <Row label="pass (fixed) · fail (fixed) · disabled">
          <div style={{ display: 'grid', gap: '0.75rem', width: '100%', maxWidth: '20rem' }}>
            <PassFailToggle value="pass" onChange={() => {}} ariaLabel="Pass example" />
            <PassFailToggle value="fail" onChange={() => {}} ariaLabel="Fail example" />
            <PassFailToggle value={null} disabled onChange={() => {}} ariaLabel="Disabled example" />
          </div>
        </Row>
      </Section>

      <Section name="SegmentedControl" file="components/ui/SegmentedControl.jsx">
        <Row label="single select">
          <SegmentedControl
            ariaLabel="Theme preference"
            value={seg}
            onChange={setSeg}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'system', label: 'System' },
            ]}
          />
        </Row>
      </Section>

      <Section name="FilterChip" file="components/ui/FilterChip.jsx">
        <Row label="active / inactive">
          {['all', 'due', 'overdue'].map((k) => (
            <FilterChip key={k} active={chip === k} onClick={() => setChip(k)}>
              {k.toUpperCase()}
            </FilterChip>
          ))}
        </Row>
      </Section>

      <Section name="AuthIcons" file="components/ui/auth/AuthIcons.jsx">
        <Row label="closed">
          <EyeIcon />
        </Row>
        <Row label="open">
          <EyeIcon open />
        </Row>
        <Row label="spinner">
          <Spinner />
        </Row>
        <Row label="spinner on dark bg">
          <div
            style={{
              background: 'var(--sidebar-bg)',
              color: 'var(--sidebar-text)',
              padding: '1rem',
              borderRadius: 'var(--radius-md)',
              display: 'inline-flex',
            }}
          >
            <Spinner />
          </div>
        </Row>
      </Section>
    </Category>
  );
}
